import { useEffect, useState, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { useGoogleMap } from '@react-google-maps/api';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { Button } from '@sm-dashboard/ui-components';

interface ReturnToBoundsButtonProps {
  bounds: google.maps.LatLngBounds | null | undefined;
  enabled?: boolean;
}

export const ReturnToBoundsButton = ({ bounds, enabled = true }: ReturnToBoundsButtonProps) => {
  const map = useGoogleMap();
  const [isOutOfBounds, setIsOutOfBounds] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const checkIfOutOfBounds = useCallback(() => {
    if (!map || !bounds || !enabled) {
      setIsOutOfBounds(false);
      return;
    }
    const mapBounds = map.getBounds();
    if (!mapBounds) return;
    const isVisible = mapBounds.intersects(bounds);
    setIsOutOfBounds(!isVisible);
  }, [map, bounds, enabled]);

  useEffect(() => {
    if (!map || !bounds || !enabled) return;
    const listener = map.addListener('bounds_changed', () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(checkIfOutOfBounds, 300);
    });
    return () => {
      google.maps.event.removeListener(listener);
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, [map, bounds, enabled, checkIfOutOfBounds]);

  const panToBounds = () => {
    if (map && bounds) {
      map.panToBounds(bounds);
      setTimeout(() => map?.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 }), 500);
    }
  };

  if (!isOutOfBounds || !bounds || !enabled) return null;

  return (
    <Box sx={{ position: 'absolute', top: 10, right: '50%', zIndex: 1000 }}>
      <Button size="large" color="secondary" startIcon={<MyLocationIcon />} onClick={panToBounds}>
        חזור לתיחום
      </Button>
    </Box>
  );
};
